(() => {
  const WIDGET_SELECTOR = "omnibus-lowest-price";
  const CARD_PRICE_SELECTOR = ".product-card__price";
  const PRODUCT_FORM_SELECTOR = ".product-form";
  const PRODUCT_PRICE_SELECTOR = ".product-price";
  const CURRENT_PRICE_SELECTOR = "[data-product-price]";
  const REGULAR_PRICE_SELECTOR = "[data-price-old]";
  const GENERATED_LINE_CLASS = "omnibus-regular-price";
  const ENHANCED_PRICE_CLASS = "omnibus-price-layout";
  const processingWidgets = new WeakSet();

  function parseMoney(value) {
    if (!value) return null;

    const normalized = value
      .replace(/\s/g, "")
      .replace(/[^\d,.-]/g, "");
    const lastComma = normalized.lastIndexOf(",");
    const lastDot = normalized.lastIndexOf(".");
    const decimalIndex = Math.max(lastComma, lastDot);

    let number = normalized;

    if (decimalIndex >= 0) {
      const integerPart = normalized.slice(0, decimalIndex).replace(/[.,]/g, "");
      const decimalPart = normalized.slice(decimalIndex + 1).replace(/[.,]/g, "");
      number = `${integerPart}.${decimalPart}`;
    }

    const parsed = Number.parseFloat(number);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function findMoney(text) {
    const match = text.match(/\d[\d\s.,]*\s*(?:zł|PLN)(?=\s|$|\()/i);

    if (!match) return null;

    return {
      formatted: match[0].trim(),
      index: match.index,
      value: parseMoney(match[0])
    };
  }

  function discountPercentage(currentPrice, referencePrice) {
    return Math.floor(((referencePrice - currentPrice) / referencePrice) * 100);
  }

  function formatDiscountPercentage(percentage) {
    return percentage > 0 ? `-${percentage}%` : "";
  }

  function resetPrice(priceContainer, regularPriceElement) {
    priceContainer.classList.remove(ENHANCED_PRICE_CLASS);
    priceContainer.querySelector(`.${GENERATED_LINE_CLASS}`)?.remove();

    const regularPriceWrapper = regularPriceElement?.parentElement;
    if (regularPriceWrapper) regularPriceWrapper.hidden = false;
  }

  function enhanceWidget(widget) {
    if (processingWidgets.has(widget)) return;
    processingWidgets.add(widget);

    try {
      const cardPrice = widget.closest(CARD_PRICE_SELECTOR);
      const productForm = widget.closest(PRODUCT_FORM_SELECTOR);
      const priceContainer = cardPrice || productForm?.querySelector(PRODUCT_PRICE_SELECTOR);
      if (!priceContainer) return;

      const currentPriceElement = priceContainer.querySelector(CURRENT_PRICE_SELECTOR);
      const regularPriceElement = priceContainer.querySelector(REGULAR_PRICE_SELECTOR);

      if (widget.querySelector(".omnibus-lowest-price-value")) return;

      resetPrice(priceContainer, regularPriceElement);

      if (!currentPriceElement || !regularPriceElement || !widget.textContent.trim()) {
        return;
      }

      const currentPrice = parseMoney(currentPriceElement.textContent);
      const regularPrice = parseMoney(regularPriceElement.textContent);
      const lowestPrice = findMoney(widget.textContent);

      if (
        currentPrice === null ||
        regularPrice === null ||
        lowestPrice?.value === null ||
        !(
          currentPrice <= lowestPrice.value &&
          lowestPrice.value <= regularPrice &&
          currentPrice < regularPrice
        )
      ) {
        return;
      }

      const omnibusDiscount = discountPercentage(currentPrice, lowestPrice.value);
      const regularDiscount = discountPercentage(currentPrice, regularPrice);
      const originalText = widget.textContent;
      const textBeforePrice = originalText.slice(0, lowestPrice.index);
      const textAfterPrice = originalText.slice(
        lowestPrice.index + lowestPrice.formatted.length
      );

      widget.replaceChildren(
        document.createTextNode(textBeforePrice),
        Object.assign(document.createElement(omnibusDiscount > 0 ? "s" : "span"), {
          className: "omnibus-lowest-price-value",
          textContent: lowestPrice.formatted
        }),
        document.createTextNode(`${textAfterPrice} `),
        Object.assign(document.createElement("span"), {
          className: "omnibus-discount-percentage",
          textContent: formatDiscountPercentage(omnibusDiscount)
        })
      );

      const regularLine = document.createElement("div");
      regularLine.className = GENERATED_LINE_CLASS;
      regularLine.append(
        document.createTextNode("Cena regularna: "),
        Object.assign(document.createElement("s"), {
          textContent: regularPriceElement.textContent.trim()
        }),
        document.createTextNode(" "),
        Object.assign(document.createElement("span"), {
          className: "omnibus-discount-percentage",
          textContent: formatDiscountPercentage(regularDiscount)
        })
      );

      if (productForm && !cardPrice) {
        const taxesElement = priceContainer.querySelector(".price__taxes-included");
        priceContainer.insertBefore(widget, taxesElement);
      }

      widget.insertAdjacentElement("afterend", regularLine);
      regularPriceElement.parentElement.hidden = true;
      priceContainer.classList.add(ENHANCED_PRICE_CLASS);
    } finally {
      processingWidgets.delete(widget);
    }
  }

  function enhanceAll(root = document) {
    if (root.matches?.(WIDGET_SELECTOR)) enhanceWidget(root);
    root.querySelectorAll?.(WIDGET_SELECTOR).forEach(enhanceWidget);
  }

  const observer = new MutationObserver((mutations) => {
    const widgets = new Set();

    mutations.forEach((mutation) => {
      const targetWidget = mutation.target.closest?.(WIDGET_SELECTOR);
      if (targetWidget) widgets.add(targetWidget);

      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(WIDGET_SELECTOR)) widgets.add(node);
        node.querySelectorAll?.(WIDGET_SELECTOR).forEach((widget) => widgets.add(widget));
      });
    });

    widgets.forEach(enhanceWidget);
  });

  function init() {
    enhanceAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
